import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Input, Row, Col, message, Switch, Card, Alert, Divider } from 'antd';
import { CreditCardOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { motion } from "framer-motion";
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

const RazorpayGateway = ({ api, authToken, navigate }) => {
    const { Permisson } = useMyContext();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [keyId, setKeyId] = useState('');
    const [keySecret, setKeySecret] = useState('');
    const [webhookSecret, setWebhookSecret] = useState('');


    const fetchGatewaySettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`${api}payment-gateway/razorpay`, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                }
            });
            const data = response.data;
            
            // Handle the response according to your table structure with credentials object
            setIsEnabled(data.isEnabled || data.status === 'active' || false);
            
            // Extract from credentials object
            const credentials = data.credentials || {};
            setKeyId(credentials.key || credentials.keyId || '');
            setKeySecret(credentials.secret || credentials.keySecret || '');
            setWebhookSecret(credentials.webhookSecret || '');

            form.setFieldsValue({
                key_id: credentials.key || credentials.keyId || '',
                key_secret: credentials.secret || credentials.keySecret || '',
                webhook_secret: credentials.webhookSecret || '',
            });
        } catch (error) {
            console.error('Error fetching Razorpay settings:', error);
        } finally {
            setLoading(false);
        }
    }, [api, authToken, form]);

    useEffect(() => {
        fetchGatewaySettings();
    }, [fetchGatewaySettings]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                gateway: 'razorpay',
                credentials: {
                    key: values.key_id,
                    secret: values.key_secret,
                    webhookSecret: values.webhook_secret
                },
                environment: 'production', // Razorpay typically uses same credentials for test/live
                isEnabled: isEnabled,
                status: isEnabled ? 'active' : 'inactive'
            };

            const response = await apiClient.post(`${api}payment-gateway/razorpay`, payload, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('Razorpay settings updated successfully!');
                // Refresh the data after successful update
                fetchGatewaySettings();
            } else {
                message.error(response.data.message || 'Failed to update Razorpay settings');
            }
        } catch (error) {
            console.error('Error updating Razorpay settings:', error);
            message.error('Error updating Razorpay settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (checked) => {
        setIsEnabled(checked);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card bordered={false} className='shadow-none'>
                <div className="d-flex align-items-center mb-4">
                    <div>
                        <h2 className="mb-0">Razorpay Gateway</h2>
                        <p className="text-muted mb-0">Configure your Razorpay payment gateway settings</p>
                    </div>
                </div>

                <Alert
                    message="Important"
                    description="Make sure to keep your API keys secure and never share them publicly. Use webhook secret for secure transaction verification."
                    type="info"
                    showIcon
                    className="mb-4"
                />

                <Row gutter={ROW_GUTTER}>
                    <Col xs={24} sm={24} md={17}>
                        <Card className="border border-light rounded-3">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Gateway Status</h5>
                                <Switch
                                    checked={isEnabled}
                                    onChange={handleSwitchChange}
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                />
                            </div>

                            <Divider />

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                className="mt-4"
                            >
                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Key ID"
                                            name="key_id"
                                            rules={[
                                                { required: true, message: 'Please enter your Razorpay Key ID' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="rzp_test_xxxxxxxxxx"
                                                value={keyId}
                                                onChange={(e) => setKeyId(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Key Secret"
                                            name="key_secret"
                                            rules={[
                                                { required: true, message: 'Please enter your Razorpay Key Secret' }
                                            ]}
                                        >
                                            <Input.Password
                                                placeholder="Enter your key secret"
                                                value={keySecret}
                                                onChange={(e) => setKeySecret(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Webhook Secret (Optional)"
                                            name="webhook_secret"
                                        >
                                            <Input.Password
                                                placeholder="Enter webhook secret"
                                                value={webhookSecret}
                                                onChange={(e) => setWebhookSecret(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item className="mt-2">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        className='float-right'
                                        icon={<SaveOutlined />}
                                    >
                                        Save Razorpay Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} sm={24} md={7}>
                        <Card className="border border-light rounded-3 bg-light">
                            <h6 className="fw-bold mb-3">Setup Instructions</h6>
                            <div className="small text-muted">
                                <ol className="ps-3">
                                    <li className="mb-2">
                                        Log in to your <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer">Razorpay Dashboard</a>
                                    </li>
                                    <li className="mb-2">
                                        Navigate to Settings → API Keys
                                    </li>
                                    <li className="mb-2">
                                        Generate or copy your Key ID and Key Secret
                                    </li>
                                    <li className="mb-2">
                                        Set up webhooks for payment status updates
                                    </li>
                                    <li className="mb-0">
                                        Test your integration in sandbox mode first
                                    </li>
                                </ol>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </motion.div>
    );
};

export default RazorpayGateway;
